/**
 * In-place edit
 */
import { useEffect, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import Text from '../../../atoms/Text';
import { formatSettingsValue } from '../helpers/settingsFormatters';

interface IParamFieldProps {
  title: string;
  value: string;
  ending?: string;
  onSave(value: number): void;
  formatValue?(value: number): string;
  validateValue?(value: number): boolean;
  getTooltip?(): JSX.Element;
}

const allowedKeys = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '0',
  '.',
  'Backspace',
  'Delete',
  'Esc',
  'Escape',
  'Left',
  'ArrowLeft',
  'Right',
  'ArrowRight',
];

export const numberOnly = (event: KeyboardEvent) => {
  if (allowedKeys.includes(event.key)) {
    // let it happen, don't do anything
    return;
  } else {
    // Ensure that it is a number and stop the keypress
    if (
      event.shiftKey ||
      ((event.keyCode < 48 || event.keyCode > 57) &&
        (event.keyCode < 96 || event.keyCode > 105))
    ) {
      event.preventDefault();
    }
  }
};

const ParamField = ({
  title,
  value,
  ending,
  getTooltip,
  onSave,
  validateValue,
  formatValue,
}: IParamFieldProps) => {
  const paramRef = useRef<HTMLSpanElement>(null);

  const handleKeyDown = (e: any) => numberOnly(e.nativeEvent);

  const handleSelect = () => {
    if (paramRef.current) {
      window.getSelection()?.selectAllChildren(paramRef.current);
      paramRef.current.focus();
    }
  };

  const handleSave = (e: any) => {
    const formattedValue = formatSettingsValue(e.target.innerText);
    if (paramRef.current) {
      if (validateValue) {
        const isValid = validateValue(formattedValue);
        if (isValid) {
          onSave(formattedValue);
          if (formatValue) {
            paramRef.current.innerText = formatValue(formattedValue);
          }
        } else {
          paramRef.current.innerText = value;
          paramRef.current.focus();
        }
      } else {
        onSave(formattedValue);
        if (formatValue) {
          paramRef.current.innerText = formatValue(formattedValue);
        }
      }
    }
  };

  useEffect(() => {
    if (paramRef.current) {
      paramRef.current.innerText = value;
    }
  }, [value]);

  return (
    <div className='params_panel__param'>
      <div className='params_panel__param__property'>
        <Text size={12}>{title}</Text>
        {getTooltip && (
          <div
            data-tip={`${title} tooltip`}
            data-for={title}
            className='params_panel__param__property__icon icon-tooltip-circle'
          />
        )}
      </div>
      {getTooltip && (
        <ReactTooltip
          id={title}
          effect='solid'
          place='left'
          className='graph_tooltip__box'
          getContent={getTooltip}
        />
      )}
      <div className='input_wrapper' onClick={handleSelect}>
        <span className='input_wrapper__editable text--12'>
          <span
            suppressContentEditableWarning
            contentEditable
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            ref={paramRef}
          >
            {value}
          </span>
          {ending && (
            <span className='input_wrapper__editable__ending' title={ending}>
              &nbsp;{ending}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default ParamField;
